import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Statistic, Row, Col, Spin, message } from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import apiClient from '../../../utils/apiClient';
import { downloadCSV } from '../../../utils/csvExport';

interface LTVData {
  line_user_id: string;
  display_name: string;
  rank_tier: string;
  gender: string;
  total_coupon_uses: number;
  total_purchase_amount: number;
  total_discount_used: number;
  avg_purchase_amount: number;
  first_use: string;
  last_use: string;
  customer_lifespan_days: number;
}

interface Summary {
  totalCustomers: number;
  totalLTV: number;
  avgLTV: number;
  totalDiscount: number;
}

const LTVAnalytics: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<LTVData[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/coupon-analytics/analytics/ltv');

      if (response.data.success) {
        setData(response.data.data.details);
        setSummary(response.data.data.summary);
      } else {
        message.error('データ取得に失敗しました');
      }
    } catch (error) {
      console.error('LTV分析エラー:', error);
      message.error('データ取得エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleExportCSV = () => {
    const csvData = data.map(row => ({
      '会員名': row.display_name || '-',
      'ランクティア': row.rank_tier || '-',
      '性別': row.gender === 'male' ? '男性' : row.gender === 'female' ? '女性' : row.gender || '-',
      'クーポン使用回数': row.total_coupon_uses,
      '累計購入金額': row.total_purchase_amount ? `¥${Math.round(row.total_purchase_amount).toLocaleString()}` : '-',
      '累計値引き額': row.total_discount_used ? `¥${Math.round(row.total_discount_used).toLocaleString()}` : '-',
      '平均購入単価': row.avg_purchase_amount ? `¥${Math.round(row.avg_purchase_amount).toLocaleString()}` : '-',
      '初回使用日': row.first_use,
      '最終使用日': row.last_use,
      '顧客寿命(日)': Math.round(row.customer_lifespan_days)
    }));

    downloadCSV(csvData, 'LTV分析.csv');
    message.success('CSVファイルをダウンロードしました');
  };

  const columns = [
    {
      title: '会員名',
      dataIndex: 'display_name',
      key: 'display_name',
      render: (text: string) => text || '-',
      ellipsis: true,
      width: 150
    },
    {
      title: 'ランク',
      dataIndex: 'rank_tier',
      key: 'rank_tier',
      render: (text: string) => text || '-',
      width: 80
    },
    {
      title: '性別',
      dataIndex: 'gender',
      key: 'gender',
      render: (text: string) => {
        if (text === 'male') return '👨';
        if (text === 'female') return '👩';
        return '-';
      },
      width: 70
    },
    {
      title: 'クーポン使用回数',
      dataIndex: 'total_coupon_uses',
      key: 'total_coupon_uses',
      align: 'right' as const,
      render: (count: number) => `${count}回`,
      sorter: (a: LTVData, b: LTVData) => a.total_coupon_uses - b.total_coupon_uses,
      width: 140
    },
    {
      title: '累計購入金額（LTV）',
      dataIndex: 'total_purchase_amount',
      key: 'total_purchase_amount',
      render: (amount: number) => (
        <span className="font-bold text-green-600">
          ¥{amount ? Math.round(amount).toLocaleString() : '0'}
        </span>
      ),
      align: 'right' as const,
      sorter: (a: LTVData, b: LTVData) => (a.total_purchase_amount || 0) - (b.total_purchase_amount || 0),
      defaultSortOrder: 'descend' as const,
      width: 160
    },
    {
      title: '累計値引き額',
      dataIndex: 'total_discount_used',
      key: 'total_discount_used',
      render: (amount: number) => (
        <span className="text-red-600">
          ¥{amount ? Math.round(amount).toLocaleString() : '0'}
        </span>
      ),
      align: 'right' as const,
      width: 130
    },
    {
      title: '平均購入単価',
      dataIndex: 'avg_purchase_amount',
      key: 'avg_purchase_amount',
      render: (amount: number) => amount ? `¥${Math.round(amount).toLocaleString()}` : '-',
      align: 'right' as const,
      width: 130
    },
    {
      title: '顧客寿命',
      dataIndex: 'customer_lifespan_days',
      key: 'customer_lifespan_days',
      render: (days: number) => {
        const roundedDays = Math.round(days);
        if (roundedDays === 0) return '新規顧客';
        return `${roundedDays}日`;
      },
      align: 'right' as const,
      sorter: (a: LTVData, b: LTVData) => a.customer_lifespan_days - b.customer_lifespan_days,
      width: 120
    }
  ];

  return (
    <div>
      {/* アクションボタン */}
      <Card className="mb-4">
        <div className="flex justify-end gap-2">
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchData}
            loading={loading}
          >
            更新
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportCSV}
            disabled={data.length === 0}
          >
            CSV出力
          </Button>
        </div>
      </Card>

      {/* 説明カード */}
      <Card className="mb-4 bg-blue-50">
        <div className="flex items-start gap-3">
          <div className="text-2xl">💡</div>
          <div>
            <div className="font-bold text-gray-800 mb-1">LTV（顧客生涯価値）分析とは？</div>
            <div className="text-sm text-gray-600">
              クーポン使用者の累計購入金額を分析します。
              どの顧客が長期的に高い価値を持つかを把握し、効果的なマーケティング施策に活かせます。
            </div>
          </div>
        </div>
      </Card>

      {loading ? (
        <Card>
          <div className="flex justify-center items-center" style={{ minHeight: 400 }}>
            <Spin size="large" />
          </div>
        </Card>
      ) : summary ? (
        <>
          {/* サマリー統計 */}
          <Row gutter={16} className="mb-4">
            <Col span={6}>
              <Card>
                <Statistic
                  title="総顧客数"
                  value={summary.totalCustomers}
                  suffix="名"
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="総LTV"
                  value={summary.totalLTV}
                  prefix="¥"
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="平均LTV"
                  value={summary.avgLTV}
                  prefix="¥"
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="総割引額"
                  value={summary.totalDiscount}
                  prefix="¥"
                  valueStyle={{ color: '#cf1322' }}
                />
              </Card>
            </Col>
          </Row>

          {/* データテーブル */}
          <Card title="顧客別LTV分析">
            <Table
              columns={columns}
              dataSource={data}
              rowKey="line_user_id"
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `全 ${total} 件`
              }}
              locale={{
                emptyText: '該当するデータがありません'
              }}
              scroll={{ x: 1200 }}
            />
          </Card>
        </>
      ) : (
        <Card>
          <div className="text-center text-gray-500 py-12">
            「更新」ボタンをクリックしてデータを読み込んでください
          </div>
        </Card>
      )}
    </div>
  );
};

export default LTVAnalytics;
